<template>
  <div v-if="issue" class="jira-issue-details">
    <div class="basic-gray-section f-h jira-issue-details__informations-wrapper">
      <CloseButton class="jira-issue-details__close-button" @click.native="$router.back()" />
      <!-- Available only on mobile -->
      <LeftDrawerPanel
        class="jira-issue-details__attachments-side-menu"
        right
        expander="/images/gallery.png"
        no-rotation
      >
        <h1 class="jira-issue-details__title basic-blue-title" style="text-align: center;">
          {{ $t('message.ISSUE_ATTACHMENTS') }}
        </h1>
        <span
          v-if="issue.attachments && !issue.attachments.length"
          class="jira-issue-details__no-attachment"
        >
          {{ $t('message.ISSUE_NO_ATTACHMENTS') }}
        </span>
        <div v-if="!issue.attachmentsLoading" class="jira-issue-details__all-attachments">
          <div v-for="item in issue.attachments" :key="item.id" class="jira-issue-details__all-attachments__attachment">
            <img
              v-if="checkAttachmentType(item.type) === 'image'"
              :src="`data:${item.type};base64, ${item.picture}`"
            >
            <div
              v-else
              class="jira-issue-details__icon-container"
            >
              <Icon
                class="jira-issue-details__icon"
                :w="80"
                :h="110"
                :color="getIcon(item.type).color"
              >
                {{ getIcon(item.type).icon }}
              </Icon>
            </div>
            <a
              class="jira-issue-details__action-icon jira-issue-details__action-icon--download"
              :href="`data:${item.type};base64, ${item.picture}`"
              target="_blank"
              :download="item.name"
            >
              <Icon>{{ downloadMedium }}</Icon>
            </a>
            <div
              class="jira-issue-details__action-icon jira-issue-details__action-icon--delete"
              @click="deleteAttachment(item.id)"
            >
              <Icon>{{ deleteMedium }}</Icon>
            </div>
            <span>{{ item.name }}</span>
          </div>
          <div
            v-if="!(issue.attachments.length >= 3)"
            class="jira-issue-details__add-attachment"
            @click="addNewAttachment"
          >
            <Icon>{{ addCircleMedium }}</Icon>
          </div>
        </div>
        <LoadingOverlay v-model="issue.attachmentsLoading" />
      </LeftDrawerPanel>
      <h1
        v-if="issue.fields.issuetype.name === 'Customer Bug'"
        class="jira-issue-details__informations-wrapper__title basic-blue-title"
      >
        {{ $t('message.ISSUE_BUG') }}
      </h1>
      <h1 v-else class="jira-issue-details__informations-wrapper__title basic-blue-title">
        {{ $t('message.ISSUE_IMPROVEMENT') }}
      </h1>
      <div class="jira-issue-details__informations-container">
        <span>{{ `${$t('message.ISSUE_ISSUE_ID')}${$t('message.TWO_DOTS')}${issue.id}` }}</span>
        <span>
          {{ `${$t('message.ISSUE_CREATION_DATE')}${$t('message.TWO_DOTS')}${issue.fields.formattedCreatedDate}` }}
        </span>
        <span v-if="issue.author">
          {{ `${$t('message.AUTHOR')}${$t('message.TWO_DOTS')}${issue.author}` }}
        </span>
      </div>
      <span :class="`jira-issue-details__status ${statusClass}`">{{ statusMessage }}</span>
      <div class="jira-issue-details__container">
        <h2 class="basic-sub-title">{{ $t('message.DESCRIPTION') }}</h2>
        <span class="jira-issue-details__description jira-issue-details">{{ issue.description }}</span>
      </div>
      <div class="jira-issue-details__container">
        <h2 class="basic-sub-title">{{ $t('message.ISSUE_DISCUSSION') }}</h2>
        <div v-if="issue.comments && issue.comments.length" class="jira-issue-details__discussion-container__comments">
          <div v-for="comment in issue.comments" :key="comment.id" class="jira-issue-details__comment">
            <span class="jira-issue-details__comment__author">{{ comment.author.displayName }}</span>
            <span class="jira-issue-details__comment__message">{{ comment.body }}</span>
            <span class="jira-issue-details__comment__date">
              {{ $moment(comment.created).format($t('dateFormat')) }}
            </span>
          </div>
        </div>
        <span v-else>{{ $t('message.ISSUE_NO_COMMENTS') }}</span>
      </div>
      <LoadingOverlay v-model="issueDetailsLoading" />
    </div>
    <div class="basic-gray-section f-h jira-issue-details__attachments-section">
      <h1 class="jira-issue-details__title basic-blue-title">{{ $t('message.ISSUE_ATTACHMENTS') }}</h1>
      <span
        v-if="issue.attachments && !issue.attachments.length"
        class="jira-issue-details__no-attachment"
      >
        {{ $t('message.ISSUE_NO_ATTACHMENTS') }}
      </span>
      <div v-if="!issue.attachmentsLoading" class="jira-issue-details__all-attachments">
        <div v-for="item in issue.attachments" :key="item.id" class="jira-issue-details__all-attachments__attachment">
          <img
            v-if="checkAttachmentType(item.type) === 'image'"
            :src="`data:${item.type};base64, ${item.picture}`"
          >
          <div
            v-else
            class="jira-issue-details__icon-container"
          >
            <Icon
              class="jira-issue-details__icon"
              :w="80"
              :h="110"
              :color="getIcon(item.type).color"
            >
              {{ getIcon(item.type).icon }}
            </Icon>
          </div>
          <a
            class="jira-issue-details__action-icon jira-issue-details__action-icon--download"
            :href="`data:${item.type};base64, ${item.picture}`"
            target="_blank"
            :download="item.name"
          >
            <Icon>{{ downloadMedium }}</Icon>
          </a>
          <div
            class="jira-issue-details__action-icon jira-issue-details__action-icon--delete"
            @click="deleteAttachment(item.id)"
          >
            <Icon>{{ deleteMedium }}</Icon>
          </div>
          <span>{{ item.name }}</span>
        </div>
        <div
          v-if="!(issue.attachments.length >= 3)"
          class="jira-issue-details__add-attachment"
          @click="addNewAttachment"
        >
          <Icon>{{ addCircleMedium }}</Icon>
        </div>
      </div>
      <LoadingOverlay v-model="issue.attachmentsLoading" />
    </div>
    <div v-if="pictureTakerVisibility" class="dark-overlay h-v-centerer">
      <div class="dark-overlay-content xlarge">
        <PictureTaker
          @close="pictureTakerVisibility = false"
          @picture="pictureTaken"
        />
      </div>
    </div>
    <!-- Hidden component -->
    <input
      ref="fileInput"
      class="jira-issue-details__hidden-input"
      type="file"
      size="0"
      @change="getUserLocalFile"
    >
  </div>
</template>

<script>
/**
 * Display Jira issue details (Description, date, author, status, comments, attachments)
 *
 * Route param : issueId : use the Jira issue id for the route
 */

import $ from 'jquery';
import toastr from 'toastr';
// Components
import CloseButton from '@/components/close-button.vue';
import Icon from '@/components/icon.vue';
import LeftDrawerPanel from '@/components/left-drawer-panel.vue';
import LoadingOverlay from '@/components/loading-overlay.vue';
import PictureTaker from '@/components/picture-taker.vue';
// States
import {
  FETCH_JIRA_ISSUE,
  FETCH_JIRA_ISSUE_ATTACHMENTS,
  FETCH_JIRA_ISSUE_COMMENTS,
  ADD_JIRA_ISSUE_ATTACHMENT,
  DELETE_JIRA_ISSUE_ATTACHMENT,
} from '@/store/modules/jira-issues/action-types';
// Util
import { calert, calertHide } from '@/utils/main';
import {
  blobToDataURL,
  getFileIconInfo,
  handlePictureDimension,
  handlePictureOrientation,
} from '@/utils/file';
// Icon
import { addCircleMedium, deleteMedium, downloadMedium } from '@/assets/icons';

export default {

  name: 'JiraIssueDetails',

  components: {
    CloseButton,
    Icon,
    LeftDrawerPanel,
    LoadingOverlay,
    PictureTaker,
  },

  data() {
    return {
      addCircleMedium,
      deleteMedium,
      downloadMedium,
      pictureTakerVisibility: false,
    };
  },

  computed: {
    /** Object: Return the current issue */
    issue() {
      return this.$store.state.jiraIssues.issues.data[`${this.$route.params.id}`];
    },
    /** String: Return a class for define the color according to the status of the request */
    statusClass() {
      switch (this.issue.statusId) {
        case 1:
          return 'jira-issue-details__status--on-going';
        case 2:
          return 'jira-issue-details__status--finished';
        case 3:
          return 'jira-issue-details__status--refused';
        default:
          return 'jira-issue-details__status--pending';
      }
    },
    /** String: Return a message according to the status of the request */
    statusMessage() {
      switch (this.issue.statusId) {
        case 1:
          return this.$t('message.ISSUE_ON_GOING');
        case 2:
          return this.$t('message.ISSUE_FINISHED');
        case 3:
          return this.$t('message.ISSUE_REFUSED');
        default:
          return this.$t('message.ISSUE_PENDING');
      }
    },
    /** Boolean: Return if the issue details and comments are loading or not */
    issueDetailsLoading() {
      return this.issue.issueLoading || this.issue.commentsLoading;
    },
  },

  created() {
    this.$store.dispatch(`jiraIssues/${FETCH_JIRA_ISSUE}`,
      { issueId: this.$route.params.id, content: 'issue' });
    this.$store.dispatch(`jiraIssues/${FETCH_JIRA_ISSUE_ATTACHMENTS}`,
      { issueId: this.$route.params.id, content: 'attachments' });
    this.$store.dispatch(`jiraIssues/${FETCH_JIRA_ISSUE_COMMENTS}`,
      { issueId: this.$route.params.id, content: 'comments' });
  },

  methods: {
    /** Get the type of the file to display the right icon */
    getIcon(iconType) {
      return getFileIconInfo(iconType);
    },
    /** Get the type of the file to display image or other file specificity */
    checkAttachmentType(type) {
      return type.startsWith('image/') ? 'image' : 'other';
    },
    /** If editPictureOnClick, clicking the picture triggers the edition modal */
    addNewAttachment() {
      const options = [
        { title: this.$t('message.TAKE_PICTURE'), action: this.takePicture },
        { title: this.$t('message.BROWSE_GALLERY'), action: this.browseGallery },
      ];
      calert(
        this.$t('message.JIRA_HOW_ADD_ATTACHMENT'),
        options,
        false,
        '',
        [{ pasteonly: true }],
        () => {
          $(document).off('paste.picture_jira_issue');
        },
      );
      $(document).on('paste.picture_jira_issue', ({ clipboardData, originalEvent }) => {
        const items = (clipboardData || originalEvent.clipboardData).items;
        Object.values(items).forEach((item) => {
          if (item.kind === 'file') {
            calertHide();
            $(document).off('paste.picture_jira_issue');
            this.formatPicture(item.getAsFile());
          }
        });
      });
    },
    /** Display the picture taker container */
    takePicture() {
      this.pictureTakerVisibility = true;
    },
    /** Send the picture that was taken and hide the picture taker container */
    pictureTaken(blob) {
      this.formatPicture(blob);
      this.pictureTakerVisibility = false;
    },
    /** Open the user file browser */
    browseGallery() {
      $(this.$refs.fileInput).trigger('click');
    },
    /** Reduce the size of the image to send it */
    formatPicture(blob) {
      return handlePictureOrientation(blob)
        .then(blobOriented => handlePictureDimension(blobOriented, 1100, 1100))
        .then((blobResized) => {
          this.sendAttachment(blobResized, blob.name);
        });
    },
    /** Convert an attachment in base64 to send it to Jira and refresh the attachments section */
    sendAttachment(blob, blobName) {
      blobToDataURL(blob)
        .then((dataURL) => {
          this.$store.dispatch(`jiraIssues/${ADD_JIRA_ISSUE_ATTACHMENT}`,
            {
              issueId: this.$route.params.id,
              attachment: dataURL,
              attachmentName: blobName,
            })
            .then(() => {
              toastr.success(this.$t('message.JIRA_UPLOAD_ATTACHMENT_SUCCESS'));
              this.$refs.fileInput.value = '';
            })
            .catch(() => {
              toastr.error(this.$t('message.CONNECTION_ERROR'));
              this.$refs.fileInput.value = '';
            });
        });
    },
    /** Delete the selected attachment and refresh the attachments section */
    deleteAttachment(attachmentId) {
      this.$store.dispatch(`jiraIssues/${DELETE_JIRA_ISSUE_ATTACHMENT}`,
        {
          issueId: this.$route.params.id,
          attachmentId,
        })
        .then(() => {
          toastr.success(this.$t('message.JIRA_DELETE_ATTACHMENT_SUCCESS'));
        })
        .catch(() => {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        });
    },
    /**
     * If the file is a picture we call the function to format it
     * Else we send directly the file
     */
    getUserLocalFile(e) {
      const file = e.target.files[0];
      if (this.checkAttachmentType(file.type) === 'other') {
        this.sendAttachment(file, file.name);
      } else {
        this.formatPicture(file, file.name);
      }
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/vars.scss';

.jira-issue-details {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;

  span {
    display: inline-block;
  }

  &__attachments-side-menu {
    display: none;
  }

  &__informations-wrapper {
    position: relative;
    width: calc(70% - 100px);
    padding: 0 3.5em;
    overflow: auto;

    .basic-sub-title {
      text-align: initial;
    }
  }

  &__informations-container {
    margin-top: 1em;

    span {
      margin-right: 1em;
    }
  }

  &__status {
    position: relative;
    margin-top: .4em;

    &::before {
      content: '';
      width: 1em;
      height: 1em;
      position: absolute;
      right: -20px;
      top: calc(50% + 1px);
      transform: translateY(-50%);
      border-radius: 10px;
    }

    &--pending::before {
      background-color: $color-jira-pending;
    }

    &--on-going::before {
      background-color: $color-jira-on-going;
    }

    &--finished::before {
      background-color: $color-jira-finished;
    }

    &--refused::before {
      background-color: $color-jira-refused;
    }
  }

  &__container {
    margin-top: 2.5em;
    background-color: #dadadaaa;
    border-radius: 6px;
    padding: 1em;

    h2 {
      margin-top: 0;
    }
  }

  &__comment {
    margin-top: 1em;
    padding-bottom: 1em;
    border-bottom: 1px solid black;

    &:last-child {
      border: none;
    }

    span {
      width: 100%;
    }

    &__author {
      font-weight: 600;
      color: $color-blue-text;
    }

    &__message {
      margin-top: .5em;
    }

    &__date {
      font-size: .9em;
      font-style: italic;
    }
  }

  &__attachments-section {
    position: relative;
    width: 28%;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow-y: auto;
    overflow-x: hidden;

    .jira-issue-details__title {
      margin-left: 1em;
    }
  }

  &__all-attachments {
    padding: 0em 2em 4em;

    &__attachment {
      position: relative;
      margin-top: 1.6em;

      img {
        width: 100%;
      }
    }
  }

  &__action-icon {
    position: absolute;
    padding: 0.5em;
    border-radius: 5em;
    height: auto;
    display: flex;
    cursor: pointer;
    opacity: .7;
    transition: .2s;

    &--download {
      right: 1em;
      top: 1em;
      background-color: #2a5a91;
    }

    &--delete {
      left: 1em;
      bottom: 2.5em;
      background-color: $color-red-bad;
    }

    &:hover {
      opacity: 1;
    }

    svg {
      position: relative;
      z-index: 1;
      color: white;
      width: 1.6em;
      height: 1.6em;
    }
  }

  &__add-attachment {
    width: 100%;
    text-align: center;
    margin-top: 1.6em;
    cursor: pointer;
    opacity: .7;
    transition: .2s;

    &:hover {
      opacity: 1;
    }

    svg {
      width: 6em;
      height: 6em;
    }
  }

  &__no-attachment {
    width: 100%;
    margin-left: 2em;
  }

  &__icon-container {
    position: relative;
    width: 100%;
    height: 10em;
    background-color: rgb(227, 229, 235);
    border-radius: 9px;
    display: flex;
    align-items: flex-end;
  }

  &__icon {
    position: absolute;
    left: 50%;
    top: 60%;
    transform: translate(-50%, -50%);

    path {
      transform: scale(3.5);
    }
  }

  &__close-button {
    position: absolute;
    top: 20px;
    left: 20px;
  }

  &__hidden-input {
    opacity: 0;
    position: absolute;
    z-index: -1;
    width: 0px;
  }

  .basic-blue-title {
    background: none;
  }
}

@media (max-width: $smartphone-landscape) {
  .jira-issue-details {

    &__informations-wrapper {
      width: calc(65% - 100px);
    }

    &__attachments-section {
      width: 35%;

      .jira-issue-details__title {
        margin-left: 1em;
      }
    }
  }
}

@media (max-width: $smartphone-portrait) {
  .jira-issue-details {
    flex-direction: column;

    &__attachments-side-menu {

      .basic-blue-title {
        margin-top: 1em;
      }
    }

    &__all-attachments {
      padding: 0 0 4em;
    }

    &__informations-wrapper {
      position: relative;
      width: calc(100% - 4em);
      min-height: calc(100vh - 3.35em);
      height: max-content;
      padding: 0 2em;
    }

    &__attachments-side-menu {
      display: block;
    }

    &__attachments-section {
      display: none;
    }

    &__no-attachment {
      margin-left: 0;
    }
  }
}

</style>
